import {
  Event,
  type ProposalDetail,
  type ProposalMetricsResponse,
  type ProposalsByStatusResponse,
  QuoteDocument,
  type QuoteDocumentList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../context/AuthContext';
import { EventClientService, QuoteDocumentClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import type { ProposalStatus } from './useProposalMetricsQuery';

function patchProposalsByStatusCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  quoteDocumentId: number,
  patch: Partial<ProposalDetail>,
) {
  const entries = Object.entries(patch) as [keyof ProposalDetail, unknown][];
  if (!entries.length) return undefined;

  let previous: ProposalDetail | undefined;
  queryClient.setQueriesData<ProposalsByStatusResponse>(
    { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'proposalsByStatus' },
    (old) => {
      if (!old?.proposals?.length) return old;
      let changed = false;
      const proposals = old.proposals.map((p) => {
        if (p.quoteDocumentId !== quoteDocumentId) return p;
        previous ??= p;
        if (entries.every(([key, value]) => p[key] === value)) return p;
        changed = true;
        return { ...p, ...patch };
      });
      return changed ? { ...old, proposals } : old;
    },
  );
  return previous;
}

const byStatusPill = (status: ProposalStatus) => (q: { queryKey: unknown }) =>
  Array.isArray(q.queryKey) && q.queryKey[0] === 'proposalsByStatus' && q.queryKey[1] === status;

// Each pill is its own server-side query, and nothing filters the rows by status downstream, so
// a declined proposal left in the Open cache keeps showing under Open wearing a Lost badge.
function dropFromProposalsByStatusPill(
  queryClient: ReturnType<typeof useQueryClient>,
  quoteDocumentId: number,
  status: ProposalStatus,
) {
  queryClient.setQueriesData<ProposalsByStatusResponse>(
    { predicate: byStatusPill(status) },
    (old) => {
      if (!old?.proposals?.length) return old;
      const proposals = old.proposals.filter((p) => p.quoteDocumentId !== quoteDocumentId);
      if (proposals.length === old.proposals.length) return old;
      // Server total, so it spans pages — the current page just renders one row short.
      return { ...old, proposals, totalCount: Math.max(0, (old.totalCount || 0) - 1) };
    },
  );
}

const isProposalStatus = (status: string): status is ProposalStatus =>
  status === 'won' || status === 'lost' || status === 'expired' || status === 'open';

// Patched rather than invalidated: the rows come from the patched `proposalsByStatus` caches,
// so refetching the counts here would leave them disagreeing with the row still on screen.
function patchProposalMetricsCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  from: ProposalStatus,
  to: ProposalStatus,
  amount: number,
) {
  if (from === to) return;

  queryClient.setQueriesData<ProposalMetricsResponse>(
    { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'proposalMetrics' },
    (old) => {
      if (!old) return old;
      const move = (status: ProposalStatus, count: number, total: number) => ({
        count: status === from ? Math.max(0, count - 1) : status === to ? count + 1 : count,
        amount:
          status === from ? Math.max(0, total - amount) : status === to ? total + amount : total,
      });
      const won = move('won', old.wonCount, old.wonAmount);
      const lost = move('lost', old.lostCount, old.lostAmount);
      const expired = move('expired', old.expiredCount, old.expiredAmount);
      const open = move('open', old.openCount, old.openAmount);
      return {
        ...old,
        wonCount: won.count,
        wonAmount: won.amount,
        lostCount: lost.count,
        lostAmount: lost.amount,
        expiredCount: expired.count,
        expiredAmount: expired.amount,
        openCount: open.count,
        openAmount: open.amount,
        conversionRate:
          won.amount + lost.amount > 0 ? (won.amount / (won.amount + lost.amount)) * 100 : 0,
      };
    },
  );
}

export type QuoteDocumentFilters = { [K in keyof QuoteDocument]?: QuoteDocument[K] };

export const useQuoteDocumentQuery = <TSelectData = QuoteDocumentList,>({
  enabled = true,
  filters = {},
  select,
}: {
  enabled?: boolean;
  filters?: QuoteDocumentFilters;
  select?: (data: QuoteDocumentList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: async () => {
      const request = QuoteDocument.create(filters);
      return QuoteDocumentClientService.BatchGet(request);
    },
    queryKey: [
      queryKeys.quoteDocument.root,
      queryKeys.quoteDocument.list,
      queryKeys.quoteDocument.getQuoteDocumentHash(filters),
    ],
    enabled,
    select,
    refetchOnWindowFocus: false,
  });
};

export const useGetQuoteDocumentQuery = ({
  enabled = true,
  eventId,
}: {
  enabled?: boolean;
  eventId: number;
}) => {
  return useQuery({
    queryFn: async () => {
      try {
        const request = QuoteDocument.create({ eventId });
        const result = await QuoteDocumentClientService.Get(request);
        return result || null;
      } catch (error) {
        if (error instanceof Error && error.message.includes('no rows in result set')) {
          return null;
        }
        throw error;
      }
    },
    queryKey: [queryKeys.quoteDocument.root, eventId],
    enabled: enabled && !!eventId,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

const markJobSeen = async (eventId: number, userId: number) => {
  const job = await EventClientService.Get(Event.create({ id: eventId }));
  if (!job || job.isResidential !== 0 || job.acknowledgedBy !== 0) return;
  await EventClientService.Update(
    Event.create({
      id: eventId,
      acknowledgedBy: userId,
      acknowledgedDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
      fieldMask: ['AcknowledgedBy', 'AcknowledgedDate'],
    }),
  );
};

export const useCreateQuoteDocumentMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (quoteDocument: QuoteDocument) => {
      return QuoteDocumentClientService.Create(quoteDocument);
    },
    onSuccess: (_created, quoteDocument) => {
      if (quoteDocument.eventId > 0) {
        markJobSeen(quoteDocument.eventId, user.id).catch(() => undefined);
      }
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteDocument.root],
        exact: false,
      });
    },
  });
};

export const useUpdateQuoteDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteDocument: QuoteDocument) => {
      return QuoteDocumentClientService.Update(quoteDocument);
    },
    onSuccess: (updatedDoc, variables) => {
      if (updatedDoc) {
        queryClient.setQueryData([queryKeys.quoteDocument.root, variables.eventId], updatedDoc);

        queryClient.invalidateQueries({
          queryKey: [queryKeys.quoteDocument.root, queryKeys.quoteDocument.list],
          exact: false,
        });

        const docId = updatedDoc.id || variables.id;
        const fieldMask = variables.fieldMask ?? [];
        if (docId) {
          const patch: Partial<ProposalDetail> = {};
          if (fieldMask.includes('LostReason')) {
            patch.lostReason = String(updatedDoc.lostReason ?? variables.lostReason ?? '');
          }
          // Value-guarded too, so an expired-but-not-lost proposal saving only a reason
          // is not relabeled 'lost'.
          if (fieldMask.includes('QuoteLost') && variables.quoteLost) {
            patch.status = 'lost';
          }
          // Patch first, so the metrics move reads the row's pre-decline status before the drop.
          const previous = patchProposalsByStatusCaches(queryClient, docId, patch);
          if (patch.status === 'lost') {
            dropFromProposalsByStatusPill(queryClient, docId, 'open');
            // The Lost pill, and the multi-dept All union that reads the same cache, need the row
            // back from the server: it is not in the Lost cache and cannot be positioned locally.
            queryClient.invalidateQueries({ predicate: byStatusPill('lost') });

            const from = (previous?.status ?? '').toLowerCase();
            if (isProposalStatus(from)) {
              patchProposalMetricsCaches(queryClient, from, 'lost', previous?.proposalAmount || 0);
            } else {
              // No cached row to contradict — the job view declines without the Proposals list.
              queryClient.invalidateQueries({ queryKey: ['proposalMetrics'] });
            }
          }
        }
      }
    },
  });
};
